import type { DocVersionSummary } from "@/lib/document/documentTypes";
import { canRestoreFromStatus } from "@/lib/document-builder/versions/versionLifecycle";
import { DocumentVersionCompare } from "./DocumentVersionCompare";

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

type Props = {
  versions: DocVersionSummary[];
  currentDraftVersionId: string | null;
  canPublish: boolean;
  canRestore: boolean;
  busy: boolean;
  compareLeftId: string;
  compareRightId: string;
  onCompareLeftChange: (id: string) => void;
  onCompareRightChange: (id: string) => void;
  onPublish: (versionId: string) => void;
  onRestore: (versionId: string) => void;
};

export function DocumentVersionPanel(props: Props) {
  const {
    versions,
    currentDraftVersionId,
    canPublish,
    canRestore,
    busy,
    compareLeftId,
    compareRightId,
    onCompareLeftChange,
    onCompareRightChange,
    onPublish,
    onRestore,
  } = props;

  const draftVersion = versions.find((v) => v.status === "draft") ?? null;

  return (
    <div className="space-y-4">
      {versions.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">
          No versions yet — scroll to Block editor and click <strong>Save first draft version</strong>.
        </p>
      ) : (
        <ul className="space-y-2">
          {versions.map((version) => {
            const isDraft = version.status === "draft";
            const restoreAllowed = canRestoreFromStatus(version.status);
            return (
              <li
                key={version.id}
                className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2 dark:border-zinc-700"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                    Version {version.version_number}
                    <span className="ml-2 text-xs font-normal capitalize text-gray-500 dark:text-zinc-400">
                      {version.status}
                    </span>
                    {version.id === currentDraftVersionId ? (
                      <span className="ml-2 text-xs text-indigo-600 dark:text-indigo-300">current draft</span>
                    ) : null}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-zinc-400">{formatWhen(version.created_at)}</p>
                  {version.change_notes ? (
                    <p className="mt-1 text-xs text-gray-600 dark:text-zinc-300">{version.change_notes}</p>
                  ) : null}
                </div>
                <div className="flex shrink-0 flex-wrap gap-1">
                  {isDraft && canPublish ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onPublish(version.id)}
                      className="rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      Publish
                    </button>
                  ) : null}
                  {restoreAllowed && canRestore ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onRestore(version.id)}
                      className="rounded-md border border-gray-300 px-2.5 py-1 text-xs dark:border-zinc-600"
                    >
                      Restore to draft
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {draftVersion && canPublish ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => onPublish(draftVersion.id)}
          className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          Publish current draft (v{draftVersion.version_number})
        </button>
      ) : null}

      {!canPublish && draftVersion ? (
        <p className="text-xs text-amber-800 dark:text-amber-200">
          Publishing requires publisher access (admin / owner). Save drafts and ask a publisher to
          release.
        </p>
      ) : null}

      {versions.length >= 2 ? (
        <DocumentVersionCompare
          versions={versions}
          leftId={compareLeftId}
          rightId={compareRightId}
          onLeftChange={onCompareLeftChange}
          onRightChange={onCompareRightChange}
        />
      ) : null}
    </div>
  );
}
